import React, { useState } from "react";

import { Button, Col, Form, InputGroup, Row, Spinner, Toast, ToastContainer } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import packageJson from "../../../../../../package.json";
import { useAuth } from "../../../../../providers";
import { DeleteAccountControllerImpl } from "../../../business/impl";
import { AccountContainerComponent } from "../components";

export const DeleteAccountPage: React.FC = () => {
    const { t } = useTranslation('account');
    const { handleLogout } = useAuth();

    const version = packageJson.version;

    const navigate = useNavigate();
    const [validated, setValidated] = useState(false);
    const [password, setPassword] = useState<string>('');
    const [passwordError, setPasswordError] = useState(false);
    const [termsError, setTermsError] = useState(false);
    const [npsError, setNpsError] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [toastingText, setToastingText] = useState<string | null>(null);
    const [showPassword, setShowPassword] = useState(false);
    const [termsChecked, setTermsChecked] = useState(false);
    const [npsText, setNpsText] = useState("");


    const controller = new DeleteAccountControllerImpl();


    const handleNpsText = (e) => {
        const text = e.target.value;
        setNpsText(text);
    };

    const handleGiveUp = () => {
        navigate('/home');
    }

    const processRequestError = (errorRequest) => {
        if (typeof errorRequest.error === 'object') {
            Object.keys(errorRequest.error).forEach(key => {
                switch (key) {
                    case "nps":
                        setNpsError(true);
                        break;
                    case "terms":
                        setTermsError(true);
                        break;
                    default:
                        setToastingText(t(key));
                }
            });
            setValidated(false);
        } else {
            setToastingText(t(errorRequest.response));
        }
    }

    const validateForm = () => {
        if (!password || password === "") {
            setPasswordError(true);
            setValidated(false);
            return false;
        }

        if (!termsChecked) {
            setPasswordError(true);
            setValidated(false);
            return false;
        }

        return true;
    };

    const prepareFormData = () => {
        const submitedFormData = new FormData();
        submitedFormData.append("password", btoa(password));
        submitedFormData.append("terms", `${termsChecked}`);
        submitedFormData.append("nps", npsText);
        return submitedFormData;
    };

    const handleRequest = async (submitedFormData) => {
        setIsLoading(true);
        try {
            const response = await controller.execute(submitedFormData);

            if (response.success) {
                localStorage.setItem('global-toast', JSON.stringify({ type: 'success', message: t('deleteAccountSuccess') }));
                const isSessionExists = await handleLogout();
                if (isSessionExists) navigate('/home', { state: { reload: true } });
                return true;
            }

            if (response.error) {
                processRequestError(response);
            }
        } catch (error) {
            setToastingText(t(error.message));
            if (error.message === 'ACCESS_DENIED') {
                setTimeout(() => {
                    navigate('/auth', { state: { reload: true } });
                }, 3000);
            }
        } finally {
            setIsLoading(false);
            window.scrollTo(0, 0);
        }

        return false;
    };

    const handleSubmit = async (event) => {
        const form = event.currentTarget;
        event.preventDefault();
        event.stopPropagation();

        setValidated(true);
        setPasswordError(false);

        if (!validateForm()) {
            setIsLoading(false);
            setValidated(false);
            return false;
        }

        const submitedFormData = prepareFormData();

        if (form.checkValidity() === false) {
            setIsLoading(false);
            setValidated(false);
            return false;
        }

        return await handleRequest(submitedFormData);
    };

    return (
        <AccountContainerComponent title={t('deleteAccountTitle')}>
            <p>{t('deleteAccountDisclaimer')}</p>
            <Form className="mt-4" onSubmit={handleSubmit} validated={validated}>
                <fieldset disabled={isLoading}>
                    <Form.Group className="mt-3 mb-3">
                        <Form.Check
                            required
                            label={t('deleteAccountTerms')}
                            feedback={t('feedback')}
                            feedbackType="invalid"
                            onChange={(e) => setTermsChecked(e.target.checked)}
                            checked={termsChecked === true ? true : false}
                            isInvalid={termsError}
                        />
                        <Form.Control.Feedback type="invalid">
                            {t('feedback_terms')}
                        </Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>{t('deleteAccountNPS')}</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            onChange={handleNpsText}
                            isInvalid={npsError}
                            required
                        />
                        <Form.Control.Feedback type="invalid">
                            {t('feedback_nps')}
                        </Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group className="mb-3" controlId="formBasicPassword">
                        <Form.Label>{t('deleteAccountPasswordLabel')}</Form.Label>
                        <InputGroup hasValidation>
                            <Form.Control
                                type={showPassword ? 'text' : 'password'}
                                required
                                minLength={6}
                                placeholder={t('placeholder_password')}
                                onChange={(e) => setPassword(e.target.value)}
                                className="custom-input"
                                isInvalid={passwordError}
                                disabled={isLoading}
                                value={password}
                            />
                            <Button variant="outline-secondary"
                                onClick={() => setShowPassword(!showPassword)}
                            >
                                {showPassword ? <FaEyeSlash /> : <FaEye />}
                            </Button>
                            <Form.Control.Feedback type="invalid">
                                {t('feedback_password')}
                            </Form.Control.Feedback>
                        </InputGroup>
                    </Form.Group>
                    <div className="row">
                        <div className="col">
                            <Button
                                variant="primary"
                                type="button"
                                className="px-4 button-flat"
                                onClick={handleGiveUp}
                                disabled={isLoading}
                            >
                                {!isLoading ? (
                                    t('deleteAccountGiveUp')
                                ) : (
                                    <Spinner animation="border" size="sm" role="status">
                                        <span className="visually-hidden">...</span>
                                    </Spinner>
                                )}
                            </Button>
                        </div>
                        <div className="col d-flex flex-row-reverse">
                            <Button
                                variant="danger"
                                type="submit"
                                className="px-4 button-flat"
                                disabled={isLoading}
                            >
                                {!isLoading ? (
                                    t('deleteAccountTitle')
                                ) : (
                                    <Spinner animation="border" size="sm" role="status">
                                        <span className="visually-hidden">...</span>
                                    </Spinner>
                                )}
                            </Button>
                        </div>
                    </div>
                    <Row>
                        <Col className="text-center mt-2"><small style={{ color: '#999999' }}>v{version}</small></Col>
                    </Row>
                </fieldset>
            </Form>
            <ToastContainer position="top-end">
                <Toast
                    bg={"danger"}
                    onClose={() => setToastingText(null)}
                    show={toastingText !== null ? true : false}
                    delay={5000}
                    autohide
                >
                    <Toast.Body className={"text-white"}>
                        {toastingText !== null ? toastingText : null}
                    </Toast.Body>
                </Toast>
            </ToastContainer>
        </AccountContainerComponent>
    );
};
